import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  DEFAULT_DRUM_BASE_NOTE,
  DEFAULT_DRUM_SAMPLE_AMP_ENV,
  DRUM_PAD_COUNT,
  cloneDrumKit,
  normalizeDrumKit,
  normalizeDrumKitMode,
  type DrumKit,
  type DrumKitMode,
  type DrumPad,
  type InstrumentSlot,
} from "../../../../../src/lib/audio";
import { useStoreSelector } from "../../../../../src/lib/store";
import { getLabActions } from "../../labActions";
import { assignSamplesToDrumPads, clearDrumPadSample } from "../../lib/drumPadAssign";
import { listImportedSampleAssets } from "../../lib/sampleLibrary";
import { projectStore } from "../../store/projectStore";
import { DrumPadGrid } from "../DrumPadGrid";
import { formatOctaveLabel, MusicalKeyboard } from "../MusicalKeyboard";
import { instrumentPresetLearnPath } from "../../controls/instrumentPresetLeaves";
import { ParamKnob } from "../ParamKnob";

/**
 * `kind: "drums"` instrument body — 8×8 pad grid + selected-pad knobs + keyboard.
 */
export function DrumEditor({ slot }: { slot: InstrumentSlot }) {
  const instruments = useStoreSelector(projectStore, s => s.instruments);
  const drumBank = useStoreSelector(projectStore, s => s.drumBank);
  const instrument = instruments[slot];
  const kit = instrument?.kind === "drums" ? normalizeDrumKit(instrument.kit) : null;
  const panelRef = useRef<HTMLDivElement>(null);
  const [selectedPad, setSelectedPad] = useState(0);
  const [octaveShift, setOctaveShift] = useState(0);
  const [sampleNames, setSampleNames] = useState<Map<string, string>>(new Map());

  useLayoutEffect(() => {
    getLabActions()?.setInstrumentSlot?.(slot);
    setSelectedPad(0);
    setOctaveShift(0);
  }, [slot]);

  useEffect(() => {
    if (!kit || normalizeDrumKitMode(kit.mode) !== "sample") {
      return;
    }
    void listImportedSampleAssets().then(assets => {
      const map = new Map<string, string>();
      for (const a of assets) {
        map.set(a.id, a.name);
      }
      setSampleNames(map);
    });
    void getLabActions()?.ensureDrumSamples?.(slot);
  }, [slot, kit?.mode, kit?.pads.map(p => p.sampleId).join("|")]);

  if (!kit) {
    return (
      <div className="muted flex min-h-0 flex-1 flex-col overflow-hidden px-3 py-2 text-[12px]">
        Slot is not a drum kit.
      </div>
    );
  }

  function commit(next: DrumKit): void {
    getLabActions()?.updateInstrumentKit?.(slot, next);
  }

  function patchKit(partial: Partial<DrumKit>): void {
    commit(normalizeDrumKit({ ...cloneDrumKit(kit!), ...partial }));
  }

  function patchPad(index: number, partial: Partial<DrumPad>): void {
    const pads = cloneDrumKit(kit!).pads;
    pads[index] = { ...pads[index]!, ...partial };
    commit(normalizeDrumKit({ ...kit!, pads }));
  }

  function auditionPad(index: number): void {
    const midi = kit!.baseNote + index;
    getLabActions()?.instrumentNoteOn?.(slot, midi, 0.9);
  }

  function setMode(mode: DrumKitMode): void {
    patchKit({ mode });
    if (mode === "sample") {
      void getLabActions()?.ensureDrumSamples?.(slot);
    }
  }

  async function assignOsFiles(startIndex: number, files: File[]): Promise<void> {
    const metas = await getLabActions()?.importLibrarySamples?.(files) ?? [];
    if (metas.length === 0) {
      return;
    }
    commit(assignSamplesToDrumPads(
      kit!,
      startIndex,
      metas.map(m => ({ id: m.id, name: m.name })),
    ));
    void getLabActions()?.ensureDrumSamples?.(slot);
  }

  const pad = kit.pads[selectedPad] ?? kit.pads[0]!;
  const sampleMode = normalizeDrumKitMode(kit.mode) === "sample";
  const sampleMissing = sampleMode && Boolean(pad.sampleId) && !sampleNames.has(pad.sampleId!);

  return (
    <div ref={panelRef} className="flex min-h-0 flex-1 flex-col overflow-hidden outline-none" tabIndex={0}>
      <div className="flex shrink-0 flex-wrap items-center gap-[0.35rem] border-b border-white/[0.06] bg-lab-panel-bar px-2 py-[0.35rem]">
        <div className="flex shrink-0 overflow-hidden rounded border border-lab-line">
          <ModeBtn active={!sampleMode} onClick={() => setMode("synth")}>Synth</ModeBtn>
          <ModeBtn active={sampleMode} onClick={() => setMode("sample")}>Sample</ModeBtn>
        </div>
        <select
          className="min-w-0 grow basis-[8rem] rounded border border-lab-line bg-lab-panel px-1.5 py-1 text-[11px] text-slate-200"
          value=""
          aria-label="Load drum kit"
          onChange={event => {
            const id = event.target.value;
            if (id) {
              getLabActions()?.applyDrumBankEntry?.(slot, id);
            }
          }}
        >
          <option value="">Load kit…</option>
          {drumBank.map(entry => (
            <option key={entry.id} value={entry.id}>{entry.name}</option>
          ))}
        </select>
        <button
          type="button"
          className="rounded border border-lab-line bg-transparent px-2 py-1 text-[11px] text-lab-muted hover:bg-white/[0.06] hover:text-slate-200"
          onClick={() => getLabActions()?.saveDrumBank?.(slot)}
        >
          Save
        </button>
        <button
          type="button"
          className="rounded border border-lab-line bg-transparent px-2 py-1 text-[11px] text-lab-muted hover:bg-white/[0.06] hover:text-slate-200"
          onClick={() => getLabActions()?.saveDrumBankAs?.(slot)}
        >
          Save as…
        </button>
        <button
          type="button"
          className="rounded border border-lab-line bg-transparent px-2 py-1 text-[11px] text-lab-muted hover:bg-white/[0.06] hover:text-slate-200"
          onClick={() => getLabActions()?.resetInstrumentSlot?.(slot)}
        >
          Reset kit
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="px-[0.6rem] py-2">
          <label className="mb-2 flex items-center gap-2 text-[11px] text-lab-muted">
            <span className="w-14 shrink-0">Name</span>
            <input
              className="min-w-0 grow rounded border border-lab-line bg-lab-panel px-2 py-1 text-[12px] text-slate-200"
              value={kit.name}
              onChange={event => patchKit({ name: event.target.value })}
            />
          </label>

          {sampleMode && (
            <p className="mb-2 text-[10px] text-lab-muted">
              Drop library samples or .wav files onto pads to assign oneshots.
            </p>
          )}

          <DrumPadGrid
            className="mb-3"
            kit={kit}
            selectedPad={selectedPad}
            onSelectPad={setSelectedPad}
            onAuditionPad={auditionPad}
            onAssignLibrarySamples={(start, samples) => {
              commit(assignSamplesToDrumPads(kit, start, samples));
              void getLabActions()?.ensureDrumSamples?.(slot);
            }}
            onAssignOsFiles={(start, files) => void assignOsFiles(start, files)}
          />

          <div className="mb-2 grid grid-cols-1 gap-[0.55rem]">
            <section className="synth-author-section rounded-lg border border-white/[0.07] bg-black/14 px-2 pt-[0.45rem] pb-[0.55rem]">
              <h3 className="mb-[0.45rem] text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
                Pad {selectedPad + 1} · MIDI {kit.baseNote + selectedPad}
                {kit.baseNote === DEFAULT_DRUM_BASE_NOTE ? " (C4)" : ""}
                {sampleMode ? " · Sample" : " · Synth"}
              </h3>
              <label className="mb-2 flex items-center gap-2 text-[11px] text-lab-muted">
                <span className="w-14 shrink-0">Name</span>
                <input
                  className="min-w-0 grow rounded border border-lab-line bg-lab-panel px-2 py-1 text-[12px] text-slate-200"
                  value={pad.name}
                  onChange={event => patchPad(selectedPad, { name: event.target.value })}
                />
              </label>
              {sampleMode && (
                <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-300">
                  <span className="text-lab-muted">Sample</span>
                  <span className="min-w-0 truncate">
                    {pad.sampleId
                      ? (sampleNames.get(pad.sampleId) ?? pad.sampleId)
                      : "— empty —"}
                  </span>
                  {pad.sampleId ? (
                    <button
                      type="button"
                      className="rounded border border-lab-line px-1.5 py-0.5 text-[10px] text-lab-muted hover:bg-white/[0.06]"
                      onClick={() => commit(clearDrumPadSample(kit, selectedPad))}
                    >
                      Clear
                    </button>
                  ) : null}
                </div>
              )}
              {sampleMissing && (
                <p className="mb-2 text-[11px] text-rose-200">
                  Sample missing — re-import or drop another onto this pad.
                </p>
              )}
            </section>

            {sampleMode ? (
              <>
                <section className="synth-author-section rounded-lg border border-white/[0.07] bg-black/14 px-2 pt-[0.45rem] pb-[0.55rem]">
                  <h3 className="mb-[0.45rem] text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
                    Oneshot
                  </h3>
                  <div className="flex flex-wrap items-start gap-x-[0.7rem] gap-y-[0.55rem]">
                    <ParamKnob
                      label="Level"
                      min={0}
                      max={1}
                      step={0.01}
                      value={pad.level}
                      defaultValue={0.85}
                      learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.level`)}
                      onChange={v => patchPad(selectedPad, { level: v })}
                    />
                    <ParamKnob
                      label="Pitch"
                      min={-24}
                      max={24}
                      step={1}
                      value={pad.pitchSemis ?? 0}
                      defaultValue={0}
                      format={v => `${v >= 0 ? "+" : ""}${Math.round(v)} st`}
                      learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.pitchSemis`)}
                      onChange={v => patchPad(selectedPad, { pitchSemis: v })}
                    />
                  </div>
                </section>
                <section className="synth-author-section rounded-lg border border-white/[0.07] bg-black/14 px-2 pt-[0.45rem] pb-[0.55rem]">
                  <h3 className="mb-[0.45rem] text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
                    Amp
                  </h3>
                  <div className="flex flex-wrap items-start gap-x-[0.7rem] gap-y-[0.55rem]">
                    {(["a", "d", "s", "r"] as const).map(key => {
                      const env = pad.ampEnv ?? DEFAULT_DRUM_SAMPLE_AMP_ENV;
                      const isSustain = key === "s";
                      return (
                        <ParamKnob
                          key={key}
                          label={key.toUpperCase()}
                          min={isSustain ? 0 : 0.001}
                          max={isSustain ? 1 : 2}
                          step={isSustain ? 0.01 : 0.001}
                          value={env[key]}
                          defaultValue={DEFAULT_DRUM_SAMPLE_AMP_ENV[key]}
                          format={v => (isSustain ? v.toFixed(2) : `${Math.round(v * 1000)} ms`)}
                          learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.ampEnv.${key}`)}
                          onChange={v =>
                            patchPad(selectedPad, {
                              ampEnv: { ...env, [key]: v },
                            })
                          }
                        />
                      );
                    })}
                  </div>
                </section>
              </>
            ) : (
              <>
                <section className="synth-author-section rounded-lg border border-white/[0.07] bg-black/14 px-2 pt-[0.45rem] pb-[0.55rem]">
                  <h3 className="mb-[0.45rem] text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
                    Body
                  </h3>
                  <div className="flex flex-wrap items-start gap-x-[0.7rem] gap-y-[0.55rem]">
                    <ParamKnob
                      label="Level"
                      min={0}
                      max={1}
                      step={0.01}
                      value={pad.level}
                      defaultValue={0.85}
                      learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.level`)}
                      onChange={v => patchPad(selectedPad, { level: v })}
                    />
                    <ParamKnob
                      label="Pitch"
                      min={20}
                      max={8000}
                      step={1}
                      value={pad.pitchHz}
                      defaultValue={120}
                      format={v => `${Math.round(v)} Hz`}
                      learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.pitchHz`)}
                      onChange={v => patchPad(selectedPad, { pitchHz: v })}
                    />
                    <ParamKnob
                      label="P.Env"
                      min={0}
                      max={4}
                      step={0.05}
                      value={pad.pitchEnv}
                      defaultValue={1.2}
                      learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.pitchEnv`)}
                      onChange={v => patchPad(selectedPad, { pitchEnv: v })}
                    />
                    <ParamKnob
                      label="Decay"
                      min={10}
                      max={2000}
                      step={5}
                      value={pad.decayMs}
                      defaultValue={180}
                      format={v => `${Math.round(v)} ms`}
                      learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.decayMs`)}
                      onChange={v => patchPad(selectedPad, { decayMs: v })}
                    />
                  </div>
                </section>
                <section className="synth-author-section rounded-lg border border-white/[0.07] bg-black/14 px-2 pt-[0.45rem] pb-[0.55rem]">
                  <h3 className="mb-[0.45rem] text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
                    Layers
                  </h3>
                  <div className="flex flex-wrap items-start gap-x-[0.7rem] gap-y-[0.55rem]">
                    <ParamKnob
                      label="Tone"
                      min={0}
                      max={1}
                      step={0.01}
                      value={pad.toneLevel}
                      defaultValue={0.7}
                      learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.toneLevel`)}
                      onChange={v => patchPad(selectedPad, { toneLevel: v })}
                    />
                    <ParamKnob
                      label="Noise"
                      min={0}
                      max={1}
                      step={0.01}
                      value={pad.noiseLevel}
                      defaultValue={0.15}
                      learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.noiseLevel`)}
                      onChange={v => patchPad(selectedPad, { noiseLevel: v })}
                    />
                    <ParamKnob
                      label="Click"
                      min={0}
                      max={1}
                      step={0.01}
                      value={pad.clickLevel}
                      defaultValue={0.25}
                      learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.clickLevel`)}
                      onChange={v => patchPad(selectedPad, { clickLevel: v })}
                    />
                    <ParamKnob
                      label="Filter"
                      min={200}
                      max={18000}
                      step={10}
                      value={pad.filterHz}
                      defaultValue={6000}
                      format={v => `${Math.round(v)} Hz`}
                      learnPath={instrumentPresetLearnPath(slot, `pads.${selectedPad}.filterHz`)}
                      onChange={v => patchPad(selectedPad, { filterHz: v })}
                    />
                  </div>
                </section>
              </>
            )}
          </div>
          <p className="mb-1 text-[10px] text-lab-muted">
            Pads map chromatically from MIDI {kit.baseNote} ({DRUM_PAD_COUNT} pads). Arm / tracker notes select pads.
          </p>
        </div>
      </div>

      <div className="shrink-0 border-t border-white/[0.06] bg-lab-panel-bar px-2 py-2">
        <div className="mb-1 flex items-center justify-between text-[10px] text-lab-muted">
          <span>Audition · {formatOctaveLabel(octaveShift)}</span>
        </div>
        <MusicalKeyboard
          key={slot}
          focusHostRef={panelRef}
          baseMidi={kit.baseNote}
          octaves={2}
          voiceMode="poly"
          onNoteOn={(note, velocity) => getLabActions()?.instrumentNoteOn?.(slot, note, velocity)}
          onNoteOff={note => getLabActions()?.instrumentNoteOff?.(slot, note)}
          onOctaveChange={setOctaveShift}
        />
      </div>
    </div>
  );
}

function ModeBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      className={[
        "px-2 py-1 text-[11px]",
        active
          ? "bg-[rgba(61,139,253,0.28)] text-white"
          : "bg-transparent text-lab-muted hover:bg-white/[0.06] hover:text-slate-200",
      ].join(" ")}
      aria-pressed={active}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
